import React from 'react';
import {
  Box,
  Heading,
  Text,
  Icon,
  Flex,
  Button,
  SimpleGrid,
  Image,
} from '@chakra-ui/react';
import { FaMoneyBillWave, FaChartLine, FaExternalLinkAlt } from 'react-icons/fa';
import BaseBountyPool from './BaseBountyPool';
import { QUICK_CASH_CONFIG } from '../Games/QuickCash/QuickCashConfig';

/**
 * QuickCashBountyPool Component
 * 
 * Custom bounty pool implementation for the Quick Cash scratch card game.
 * Extends the BaseBountyPool with Quick Cash specific styling and data.
 */
const QuickCashBountyPool = () => {
  // Get bounty pool data from Quick Cash config
  const { bountyPool } = QUICK_CASH_CONFIG;
  
  // Quick Cash specific styling
  const quickCashStyles = {
    accentColor: "yellow.400",
    bgColor: "gray.900", // Darker black to match the Quick Cash theme
    gradientBg: true,
    columnHeaders: {
      remaining: "REMAIN" // Use 'REMAIN' instead of 'REMAINING'
    }
  };
  
  // Quick Cash specific header
  const renderHeader = () => (
    <Flex align="center" mb={4}>
      <Icon as={FaMoneyBillWave} boxSize={8} color="yellow.400" mr={3} />
      <Box>
        <Heading as="h3" size="lg" color="yellow.400">
          QUICK CASH Bounty Pool
        </Heading>
        <Text color="gray.300" fontSize="sm">
          Fast and easy wins up to 100,000 JINU!
        </Text>
      </Box>
    </Flex>
  );
  
  // Quick Cash specific stats section
  const renderStats = () => {
    // Calculate total winners
    let totalWinners = 0;
    let topPrizes = [];
    
    if (bountyPool && bountyPool.tiers) {
      // Process tiers to get total winners and top prizes
      bountyPool.tiers.forEach(tier => {
        tier.prizes.forEach(prize => {
          totalWinners += prize.count;
          
          // Add top prizes for display (limit to 6 for UI)
          if (tier.name === 'Top' || tier.name === 'Mid') {
            topPrizes.push({
              amount: prize.amount,
              count: prize.count
            });
          }
        });
      });
      
      // Sort prizes by amount (highest first)
      topPrizes.sort((a, b) => b.amount - a.amount);
      // Limit to 6 for display
      topPrizes = topPrizes.slice(0, 6);
    }
    
    // Calculate RTP percentage
    const rtpPercentage = bountyPool ? 
      Math.round((bountyPool.totalPrizePool / bountyPool.totalSales) * 100) : 75;
    
    return (
      <Box mt={6} mb={8} p={4} bg="linear-gradient(135deg, rgba(0,0,0,0.8), rgba(50,50,0,0.2))" borderRadius="md" boxShadow="xl">
        <Heading as="h4" size="md" mb={4} color="yellow.400">
          QUICK CASH Prize Distribution
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box>
            <Box p={3} borderRadius="md" bg="rgba(0,0,0,0.2)" mb={3}>
              <Flex justify="space-between" mb={2}>
                <Text color="gray.300">Total Sales:</Text>
                <Text color="white" fontWeight="bold">{bountyPool?.totalSales?.toLocaleString() || '2,000,000'} JINU</Text>
              </Flex>
              <Flex justify="space-between" mb={2}>
                <Text color="gray.300">Total Prize Pool:</Text>
                <Text color="green.300" fontWeight="bold">
                  {bountyPool?.totalPrizePool?.toLocaleString() || '1,500,000'} JINU ({rtpPercentage}% RTP)
                </Text>
              </Flex>
            </Box>
            
            <Box p={3} borderRadius="md" bg="rgba(255,255,0,0.1)" mb={3}>
              <Flex justify="space-between" mb={2}>
                <Text color="gray.300">Minimum Prize:</Text>
                <Text color="yellow.300" fontWeight="bold">{bountyPool?.minPrize || 200} JINU</Text>
              </Flex>
              <Flex justify="space-between">
                <Text color="gray.300">Total Winners:</Text>
                <Text color="yellow.300" fontWeight="bold">{totalWinners.toLocaleString()} Cards</Text>
              </Flex>
            </Box>
            
            <Box p={3} borderRadius="md" bg="rgba(128,0,128,0.1)">
              <Text color="purple.300" fontWeight="bold" mb={2}>Lottery Entries:</Text>
              <Flex justify="space-between" mb={1}>
                <Text color="gray.300">Winning Cards:</Text>
                <Text color="green.300">{totalWinners.toLocaleString()} Cards</Text>
              </Flex>
              <Flex justify="space-between" mb={1}>
                <Text color="gray.300">Losing Cards:</Text>
                <Text color="yellow.300">
                  {bountyPool?.losingTickets?.count?.toLocaleString() || '5,402'} Cards
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text color="gray.300">Entries per Losing Card:</Text>
                <Text color="purple.300" fontWeight="bold">
                  {bountyPool?.losingTickets?.lotteryEntries?.min || 1}-
                  {bountyPool?.losingTickets?.lotteryEntries?.max || 5} entries
                </Text>
              </Flex>
            </Box>
          </Box>
          <Box>
            <Text color="yellow.400" fontWeight="bold" mb={2}>Top Prize Tiers:</Text>
            {topPrizes.map((prize, index) => {
              // Determine color based on prize amount
              let prizeColor;
              let bgGradient = false;
              
              if (prize.amount >= 100000) {
                prizeColor = "lime.500"; // Lime green for top prize
                bgGradient = true;
              } else if (prize.amount >= 50000) {
                prizeColor = "yellow.400";
                bgGradient = true;
              } else if (prize.amount >= 20000) {
                prizeColor = "orange.400";
                bgGradient = true;
              } else if (prize.amount >= 10000) {
                prizeColor = "red.400";
                bgGradient = true;
              } else if (prize.amount >= 5000) {
                prizeColor = "pink.400";
              } else if (prize.amount >= 2500) {
                prizeColor = "purple.400";
              } else if (prize.amount >= 1000) {
                prizeColor = "blue.400";
              } else if (prize.amount >= 500) {
                prizeColor = "cyan.400";
              } else if (prize.amount >= 300) {
                prizeColor = "teal.400";
              } else {
                prizeColor = "green.400";
              }
              
              // Get lottery entries from the configuration
              // Find the prize in the config to get its entries
              let entries = 1; // Default value
              
              if (bountyPool && bountyPool.tiers) {
                // Look through all tiers to find this prize
                bountyPool.tiers.forEach(tier => {
                  tier.prizes.forEach(configPrize => {
                    if (configPrize.amount === prize.amount) {
                      entries = configPrize.entries || entries;
                    }
                  });
                });
              }
              
              return (
                <Flex key={index} justify="space-between" mb={2} p={2} borderRadius="md" 
                  bg={bgGradient ? 
                    `linear-gradient(45deg, ${prizeColor}, ${prizeColor.replace('.500', '.700')})` : 
                    `${prizeColor}25`
                  }
                >
                  <Box>
                    <Text color={bgGradient ? "white" : prizeColor} fontWeight="bold">
                      {prize.amount.toLocaleString()} JINU
                    </Text>
                    <Text color="gray.300" fontSize="xs">Entries: {entries}</Text>
                  </Box>
                  <Box textAlign="right">
                    <Text color={bgGradient ? "white" : "gray.100"}>{prize.count} {prize.count === 1 ? 'Card' : 'Cards'}</Text>
                    <Text color="gray.300" fontSize="xs">Odds: 1:{Math.round(10000/prize.count)}</Text>
                  </Box>
                </Flex>
              );
            })}
          </Box>
        </SimpleGrid>
      </Box>
    );
  };

  // Quick Cash specific footer with other games showcase
  const renderFooter = () => (
    <Box mt={8}>
      <Heading as="h4" size="md" mb={4} color="white">
        Ready to Try Your Luck?
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
        <Box 
          bg="green.900" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>CASH KING</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 100,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Play Now
          </Button>
        </Box>
        <Box 
          bg="pink.900" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>JACKPOT MILLIONS</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 250,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Play Now
          </Button>
        </Box>
        <Box 
          bg="blue.900" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>MEGA JINU SCRATCH</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 10,000,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Coming Soon
          </Button>
        </Box>
      </SimpleGrid>
    </Box>
  );
  
  // Construct the prize distribution data from the bounty pool tiers
  const constructPrizeDistribution = () => {
    const prizeDistribution = [];
    
    if (bountyPool && bountyPool.tiers) {
      // Flatten all prizes from all tiers
      const allPrizes = [];
      bountyPool.tiers.forEach(tier => {
        tier.prizes.forEach(prize => {
          allPrizes.push(prize);
        });
      });
      
      // Sort by amount (highest first)
      allPrizes.sort((a, b) => b.amount - a.amount);
      
      // Convert to the format expected by BaseBountyPool
      allPrizes.forEach(prize => {
        // Calculate claimed and remaining based on some percentage (for demo)
        const claimed = Math.floor(prize.count * 0.3); // 30% claimed for demo
        const remaining = prize.count - claimed;
        
        prizeDistribution.push({
          prize: `${prize.amount.toLocaleString()} JINU`, // Format as "100,000 JINU"
          total: prize.count,
          claimed: claimed,
          remaining: remaining,
          odds: `1:${Math.round(10000/prize.count)}`,
          entries: prize.entries
        });
      });
    }
    
    return prizeDistribution;
  };
  
  // Enhanced pool data with prize distribution
  const enhancedPoolData = {
    ...bountyPool,
    batchNumber: "#001",
    startDate: "April 2025",
    cardsMinted: 4632,
    cardsRemaining: 5368,
    totalCards: 10000,
    usdValue: 15000,
    winners: 432,
    blockHash: "5d8c6e9b3a2f1d4e8c7b6a5d9e8f7c6b5a4d3e2f1c",
    blockNumber: "18,456,932",
    lastVerified: "2 mins ago",
    prizeDistribution: constructPrizeDistribution()
  };
  
  return (
    <Box>
      {renderHeader()}
      {renderStats()}
      <BaseBountyPool 
        poolData={enhancedPoolData}
        accentColor={quickCashStyles.accentColor}
        bgColor={quickCashStyles.bgColor}
        columnHeaders={quickCashStyles.columnHeaders}
        prizeColorMap={{
          100000: "gold",
          50000: "yellow.400",
          25000: "orange.400",
          10000: "red.400",
          5000: "pink.400",
          2000: "purple.400",
          1000: "blue.400",
          500: "cyan.400",
          400: "teal.400",
          300: "green.400",
          200: "green.500",
          100: "blue.500"
        }}
      />
      {renderFooter()}
    </Box>
  );
};

export default QuickCashBountyPool;
